import { useEffect, useRef, useState } from 'react';

import type { Birthday, ChoirBrief, MyAttendance, VoicePart } from '@contracts/index';

import { ApiError, api, type Avatar as AvatarChoice } from '../api';
import { plural, voicePartName } from '../format';
import { PencilIcon } from '../icons';
import { askWriteAccess } from '../telegram';
import { useBackKey } from '../backKey';
import Avatar, {
  AVATAR_LARGE,
  COLOR_COUNT,
  EYES_COUNT,
  EYES_GLYPH,
  EyesGlyph,
  SMILE_STEPS,
  smileOf,
  type Smile,
} from './Avatar';
import BirthdayWheel from './BirthdayWheel';
import {
  LADDER,
  nextRung,
  nextSmileLine,
  openCount,
  rungJustReached,
  rungOf,
  type Rung,
  type RungKind,
} from './ladder';

/**
 * **The card about one face, and «Профиль» behind it, from
 * `legacy/frontend/src/screens/MyProfileCard.tsx` — a region port**
 * (`docs/specs/features/F-021/plan.md` T6 and T7, `docs/specs/features/F-022/plan.md` T6).
 *
 * The file is the old one from its `:38` down to `:673`: the card with its doc comment,
 * `SMILE_NAMES` and `tenureLine` (F-021's), then `smileHint`, `eyesLabel`, `colorLabel`, the
 * draft type and **`ProfileScreen`** with its two pickers, its own back key and the one write on
 * this screen (F-022's, every comment included).
 *
 * **F-030** (`docs/specs/features/F-030/plan.md` T4) adds the tail those two left, and nothing
 * beside it: **`RemindersRow`** with the doc comment over it and its four recorded decisions, the
 * three pieces of state, `allowReminders` and the two shapes the permission has (`:675-737`), and
 * `askWriteAccess` in the `../telegram` import (`:14`) — **this tree's first caller of it**. The
 * old file has nothing under that, so this one now runs to its last line.
 *
 * `tenureLine` is **the second function of that name in the old app**, and it is ported as
 * its own function rather than imported from `app/src/screens/MembersCard.tsx`, whose one is
 * the roster's: this one takes the part and the years and says «бас · в хоре 4 года», that
 * one takes a row and says «первый год» with no part in front of it. **`FND-820`, `KEEP`** —
 * the shape `FND-509` already settled for the two sub-lines. Neither imports the other.
 *
 * Two of the old card's lines are read by review rather than by a test, and the test file
 * says so where it says what it asserts: `` `ещё ${Math.max(0, rung.at - stats.streak)}` ``
 * (legacy `:173`), whose clamp cannot fire because `next` is the first rung with
 * `best_streak < at` and `streak <= best_streak` — **`FND-819`**, ported as written — and
 * `key={rung.at}` (legacy `:165`) on four rows of a fixed length in a fixed order.
 * The other oddity here is **`FND-806`**, `KEEP`, and it is visible: `pct` rounds and
 * `smileOf` compares the rate itself, so at four rates the sentence and the mouth disagree.
 * `ProfileScreen`'s `if (rung === undefined) return;` in `refuse` (legacy `:405`) is a third,
 * reached by no cell — `ProfileScreen.test.tsx` says why and what the mutation measured.
 *
 * The imports differ from the old file's on purpose: the server shapes come from
 * `@contracts/index` (ADR-0011) rather than from `../api`, which is where the old client kept
 * its own copy of each of them — `ApiError`, `api` and the `Avatar` pair stay there, being the
 * client's own.
 *
 * The regions of the old file this one reproduces line for line are on
 * `scripts/ported-partial.txt`.
 */

/**
 * One card about one face, at the top of «Я».
 *
 * One card since 2026-09-04, and until that evening it was two: a row naming
 * the person, and a block under the figures explaining their mouth and listing
 * what a streak opens. They were one object drawn twice - one face, one
 * sentence about its mouth, one ladder about its gifts - and the screen carried
 * two avatars for it, 44 in the row and 64 in the block. The heading «ЛИЦО»
 * went with the merge: a card about the face is captioned by the name of whose
 * face it is.
 *
 * It stands **above** the segment, so it no longer disappears on «Участия». It
 * always counts rehearsals - the face follows practices, and it did that in the
 * row already - and a card that vanished when somebody tapped «Участия» was
 * saying the person had gone with it.
 *
 * The face and the figures arrive apart on purpose: the name and the part are
 * in hand from the first frame, the mouth and the ladder need a request. Waiting
 * for the second to draw the first would put a hole where the person is.
 *
 * Before any of this the name was typed once when joining and could be changed
 * by nobody: a typo stayed in every matrix forever, and two people called Anna
 * were indistinguishable. The pencil hangs off the face because the face is
 * what people come back to change.
 */
export default function MyProfileCard({
  choir,
  stats,
  onOpenProfile,
}: {
  choir: ChoirBrief;
  /** Rehearsals, whichever half of the segment is showing; null until they load. */
  stats: MyAttendance | null;
  /** Into «Профиль», which is a screen and not a panel. */
  onOpenProfile: () => void;
}) {
  const rate = stats?.attendance_rate_30d ?? null;
  const pct = rate === null ? 0 : Math.round(rate * 100);
  const smile = smileOf(rate);
  const next = stats === null ? undefined : nextRung(stats.best_streak);
  return (
    <section className="card face-block">
      <div className="profile">
        <button className="profile-face" onClick={onOpenProfile} aria-label="Изменить профиль">
          {/* Not tappable: this face is the button with the pencil on it, and a
              face that preened instead of opening «Профиль» would be the app
              taking a tap and handing back a flourish. */}
          <Avatar
            eyes={choir.avatar_eyes}
            color={choir.avatar_color}
            rate={rate}
            size={AVATAR_LARGE}
            tappable={false}
          />
          <span className="profile-face-badge">
            <PencilIcon size={12} />
          </span>
        </button>
        <div className="live-body">
          <h1 className="profile-name">
            {choir.display_name}
            {/* A mark and not a second plaque: the news is said in words at the
                top of the screen, and this is a dot on the thing it is about.
                The same condition the plaque hangs on, so the two arrive and
                leave together. There is no third copy of the words - the rung
                is already named in the ladder below, with «открыто» on it. */}
            {stats !== null && rungJustReached(stats.streak) !== undefined && (
              <span className="profile-name-dot" />
            )}
          </h1>
          {/* The part and the years, and not the percentage: that figure is
              said in full two lines below, in the sentence that explains what
              it does. «Б·4·1», the avatar written out, is off the screen
              entirely: it was the only coloured thing in the header and
              therefore the loudest, and what it named was a serial number. */}
          <p className="screen-caption">{tenureLine(choir.voice_part, choir.tenure_years)}</p>
        </div>
      </div>

      {stats !== null && (
        <>
          <div className="face-rule" />

          {/* Why the mouth is what it is. The share is drawn as a length with
              the four thresholds ticked on it, because what somebody wants from
              that figure is not its value but how far the next step is. */}
          <div className="face-block-said">
            <p className="face-line">
              {rate === null
                ? 'Пока нечего считать: за 30 дней не было ни одной закрытой спевки.'
                : smile === 'sad'
                  ? `Грусть — ${pct}% за 30 дней. Лицо грустит, пока доля ниже 40%.`
                  : `${SMILE_NAMES[smile]} — ${pct}% за 30 дней. Улыбку ставит посещаемость, её не выбирают.`}
            </p>
            {rate !== null && (
              <>
                {/* The four thresholds as ticks on the track: the numbers the
                    mouth changes at, in the one place where drawing them is not
                    a table of rules but the answer to «сколько ещё». Read off
                    `SMILE_STEPS` rather than written out, so a tick can never
                    stand anywhere but where the mouth actually changes. */}
                <div className="face-track">
                  <span className="face-track-fill" style={{ width: `${pct}%` }} />
                  {SMILE_STEPS.map(([at]) => (
                    <span key={at} className="face-tick" style={{ left: `${at}%` }} />
                  ))}
                </div>
                <p className="face-next">{nextSmileLine(pct)}</p>
              </>
            )}
          </div>

          <div className="face-rule" />

          <div className="face-ladder">
            {/* «сейчас», because the four figures under this label are rungs and
                this one is not: without the word the reader has «7» over a
                column of 5, 10, 15, 20 and no way to tell which kind of number
                it is. */}
            <span className="label">
              За серию подряд · сейчас <span className="section-count">{stats.streak}</span>
            </span>
            {LADDER.map((rung) => {
              const done = stats.best_streak >= rung.at;
              const isNext = next?.at === rung.at;
              return (
                <div
                  key={rung.at}
                  className={`ladder-row ladder-row--${done ? 'done' : isNext ? 'next' : 'later'}`}
                >
                  <span className="ladder-at">{rung.at}</span>
                  <span className="ladder-gift">{rung.gift}</span>
                  {/* A rung further off than the next one says nothing: it is
                      not «ещё 12», it is not yet anybody's business. */}
                  <span className="ladder-state">
                    {done ? 'открыто' : isNext ? `ещё ${Math.max(0, rung.at - stats.streak)}` : ''}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * The step of the smile as the sentence about it starts.
 *
 * A `Record` and not an if-chain with a fallback: a sixth step would have
 * quietly come out as «Ровный рот», and this way the compiler asks what it is
 * called. `sad` is here for completeness and is never read - the sad sentence
 * is written out in full, because it also has to say where the floor is.
 */
const SMILE_NAMES: Record<Smile, string> = {
  wide: 'Широкая улыбка',
  smile: 'Улыбка',
  half: 'Полуулыбка',
  flat: 'Ровный рот',
  sad: 'Грусть',
};

/** «бас · в хоре 4 года», or the part alone in somebody's first year. */
function tenureLine(part: VoicePart | null, years: number): string {
  if (years < 1) return `${voicePartName(part)} · первый год`;
  return `${voicePartName(part)} · в хоре ${years} ${plural(years, ['год', 'года', 'лет'])}`;
}

/**
 * The sentence under the large face: what sets the mouth, and where the floor
 * is. The floor is named only where there is a figure to compare it with — with
 * nothing counted yet «ниже 40%» is a threshold on an empty scale.
 */
function smileHint(rate: number | null): string {
  if (rate === null) return 'Улыбку ставит посещаемость, её не выбирают: пока нечего считать.';
  return (
    `Улыбку ставит посещаемость, её не выбирают: сейчас ${Math.round(rate * 100)}% за 30 дней. ` +
    'Ниже 40% лицо грустит.'
  );
}

/**
 * What a cell is called for somebody who cannot see it.
 *
 * The two earned pairs are named rather than numbered — the rule of 2026-09-04
 * for every string a person reads, and a label read aloud is one. The six that
 * everybody has from the door have no names to give: they are told apart by
 * looking, and «Глаза 3» is the only thing left to say about them.
 */
function eyesLabel(index: number, locked: Rung | undefined): string {
  const named = rungOf('eyes', index);
  const what = named === undefined ? `Глаза ${index}` : `Глаза — ${named.name}`;
  return locked === undefined ? what : `${what}, откроет серия ${locked.at} подряд`;
}

function colorLabel(index: number, locked: Rung | undefined): string {
  const named = rungOf('color', index);
  const what = named === undefined ? `Цвет ${index}` : `Цвет — ${named.name}`;
  return locked === undefined ? what : `${what}, откроет серия ${locked.at} подряд`;
}

/** Everything about the person, held together so one save can carry all of it. */
type Draft = {
  name: string;
  voicePart: VoicePart | null;
  birthday: Birthday | null;
  avatar: AvatarChoice;
  /** Whether this person stands in the queue of sermons. null — not stated. */
  isBrother: boolean | null;
};

/**
 * «Профиль»: the two channels of the face, the name, the date, and the two
 * facts the regent keeps.
 *
 * In that order since 2026-08-29, and the order is the point: what somebody
 * opens this screen for is the face, and the eyes and the colour stood last,
 * after two rows nobody on this screen can change.
 *
 * One screen and one edit, since 2026-08-21. There were two - a form of three
 * fields unfolding inside the card on «Я», and the avatar constructor behind a
 * row under it - and between them they split the person in half: the part was
 * in the form, the face that is drawn from the part was on the other screen.
 *
 * A screen rather than a panel, for the reason the constructor was one already:
 * what is chosen here is chosen once, and it was standing above the figure «Я»
 * exists for on every visit afterwards.
 *
 * There is no «Сохранить / Отмена» pair, and since 2026-09-01 no «Готово»
 * either. Everything that is picked - the part, the date, the eyes, the colour
 * - saves on the tap that picks it, because there is nothing to confirm about
 * picking a pair of eyes. The name is the one thing that is typed rather than
 * picked, so it saves when the field loses the focus, and again on the way out
 * for a name that never lost it. The button went because it did nothing the
 * client's own arrow does not do, and two ways out of one screen is what the
 * owner asked to be rid of.
 */
export function ProfileScreen({
  choir,
  rate,
  streak,
  best,
  onChanged,
  onBack,
}: {
  choir: ChoirBrief;
  rate: number | null;
  /**
   * The streak of rehearsals now, and the record. The first says how far a
   * locked cell is; the second is what actually opens one, so that a week of
   * flu never takes back what was earned. Both are 0 until «Я» has its figures.
   */
  streak: number;
  best: number;
  onChanged: () => void;
  onBack: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  /**
   * What a tap on a locked cell said, in the accent, instead of the sentence
   * about the smile. It goes on the next successful pick: the refusal was about
   * the cell that was tapped, and the screen has moved on from it.
   */
  const [note, setNote] = useState<string | null>(null);
  /** Which locked cell is refusing right now — the timestamp remounts it. */
  const [shake, setShake] = useState<string | null>(null);
  /** A pick just happened: the large face plays `squash`, keyed by the moment. */
  const [squash, setSquash] = useState(0);

  useBackKey(onBack);
  const [draft, setDraft] = useState<Draft>(() => ({
    name: choir.display_name,
    voicePart: choir.voice_part,
    // ?? null rather than a bare read: a server that predates the field sends
    // no key at all, and undefined would walk straight past every `=== null`
    // below into the wheel as a date.
    birthday: choir.birthday ?? null,
    avatar: { eyes: choir.avatar_eyes, color: choir.avatar_color },
    isBrother: choir.is_brother ?? null,
  }));

  // What the server holds. The name field may have been typed into since, and
  // that difference is the whole question the blur and the way out answer.
  const savedName = useRef(choir.display_name);
  const latest = useRef(draft);
  latest.current = draft;
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  // The backstop under the blur: the client's arrow takes the screen away while
  // the field may still hold the focus, and nobody types a name meaning to
  // throw it away. A name the blur already saved equals the saved one by now,
  // so this does not send it twice - and the same guard is what keeps it from
  // firing on React's development remount, where nothing was typed at all.
  useEffect(
    () => () => {
      const { name, voicePart, birthday, avatar, isBrother } = latest.current;
      const trimmed = name.trim();
      if (trimmed === '' || trimmed === savedName.current) return;
      void api
        .updateMemberProfile(choir.member_id, trimmed, voicePart, avatar, birthday, isBrother)
        .then(() => onChangedRef.current())
        .catch(() => {
          // The screen this belonged to is gone; there is nowhere to say so.
        });
    },
    [choir.member_id],
  );

  async function save(next: Draft): Promise<boolean> {
    // A blank field is not an instruction to erase the name: the server refuses
    // it, and a picked voice part would go down with it.
    const name = next.name.trim() === '' ? savedName.current : next.name.trim();
    setBusy(true);
    setFailure(null);
    try {
      const updated = await api.updateMemberProfile(
        choir.member_id,
        name,
        next.voicePart,
        next.avatar,
        next.birthday,
        next.isBrother,
      );
      savedName.current = updated.display_name;
      // Everything but the name comes back from the server. The name does not:
      // a save fired by a wheel while somebody is still typing would otherwise
      // put the field back to what it held when the request left.
      setDraft((current) => ({
        ...current,
        voicePart: updated.voice_part,
        birthday: updated.birthday,
        avatar: { eyes: updated.avatar_eyes, color: updated.avatar_color },
        isBrother: updated.is_brother,
      }));
      onChanged();
      return true;
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'Не удалось сохранить');
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** Something was picked: the screen shows it at once and the save follows. */
  function pick(patch: Partial<Draft>): void {
    const next = { ...draft, ...patch };
    setDraft(next);
    void save(next);
  }

  const openEyes = openCount('eyes', best);
  const openColors = openCount('color', best);
  /**
   * The rung the person is standing on, if any: its cell is the one that has
   * just opened, and it pops with a ring going out from it. Read once per mount
   * and never restarted — it is the arrival of the screen that shows it, the
   * same rule the milestone card on «Я» follows.
   */
  const reached = rungJustReached(streak);

  /** A tap on a cell nothing has opened: the cell refuses and the hint says why. */
  function refuse(kind: RungKind, index: number): void {
    const rung = rungOf(kind, index);
    if (rung === undefined) return;
    setShake(`${kind}${index}:${Date.now()}`);
    setNote(
      `${kind === 'eyes' ? 'Эти глаза' : 'Этот цвет'} откроет серия ${rung.at} подряд — сейчас ${streak}.`,
    );
  }

  /** A tap on a cell the streak has opened: it saves, and the face plops. */
  function choose(patch: Partial<Draft>): void {
    setNote(null);
    setSquash(Date.now());
    pick(patch);
  }

  return (
    // The name of the screen, for a reader who has only the name. It is said
    // here rather than drawn: the face and the name under it are what tell
    // somebody looking at the screen where they are.
    <section className="screen screen--form" aria-label="Профиль">
      {/* The face being built, at the size the choir sees it grown to the size
          somebody making a choice needs. */}
      <div className="avatar-stage">
        {/* Remounted on every pick, because an animation does not restart when
            the class that runs it is put back on the same element. The rung's
            own jump wins over the plop: the screen was opened to see it. */}
        <Avatar
          key={reached === undefined ? squash : 'rung'}
          eyes={draft.avatar.eyes}
          color={draft.avatar.color}
          rate={rate}
          size={AVATAR_LARGE}
          move={reached !== undefined ? 'hop' : squash === 0 ? null : 'squash'}
          mine
          streak={streak}
        />
      </div>

      {/* The third channel of the face, said as a sentence under it rather than
          drawn as a row that looks like the two below and is not a choice. It
          is here at all because somebody who cannot find where to change the
          smile goes looking for it. The refusal a locked cell gives takes the
          same line, in the accent: it is an answer about the face, and a second
          sentence appearing under the first would push the grids down. */}
      <p className={note === null ? 'hint hint--center' : 'hint hint--center hint--note'}>
        {note ?? smileHint(rate)}
      </p>

      {/* The two channels of the face, directly under the face they build.
          They stood at the foot of the screen until 2026-08-29, after the name,
          the date and the two facts the regent keeps, where the thing this
          screen is most often opened for read as the tail of a form. */}
      <div className="picker-row">
        {/* «Глаза» and not «Взгляд»: the cells draw the word literally, and it
            is the word the avatar describes itself with. Beside «Имя» and
            «Цвет» it is a noun for a thing, as they are. */}
        <span className="picker-label">Глаза</span>
        <div className="picker">
          {Array.from({ length: EYES_COUNT }, (_, index) => index + 1).map((eyes) => {
            const locked = eyes > openEyes;
            const rung = rungOf('eyes', eyes);
            const justOpened = reached?.kind === 'eyes' && reached.index === eyes;
            const shaking = shake?.startsWith(`eyes${eyes}:`) === true;
            return (
              <button
                key={eyes}
                className={
                  [
                    'picker-cell',
                    eyes === draft.avatar.eyes ? 'picker-cell--picked' : null,
                    locked ? 'picker-cell--locked' : null,
                    justOpened ? 'picker-cell--flash' : null,
                  ]
                    .filter((name) => name !== null)
                    .join(' ')
                }
                aria-label={eyesLabel(eyes, locked ? rung : undefined)}
                aria-pressed={eyes === draft.avatar.eyes}
                disabled={busy}
                onClick={() =>
                  locked ? refuse('eyes', eyes) : choose({ avatar: { ...draft.avatar, eyes } })
                }
              >
                {/* Keyed by what is playing, so a second refusal on the same
                    cell shakes it again rather than doing nothing. */}
                <span
                  className={
                    [
                      'picker-move',
                      shaking ? 'picker-move--shake' : null,
                      justOpened ? 'picker-move--pop' : null,
                    ]
                      .filter((name) => name !== null)
                      .join(' ')
                  }
                  key={shaking ? (shake as string) : 'still'}
                >
                  <EyesGlyph eyes={eyes} size={EYES_GLYPH} />
                </span>
                {locked && rung !== undefined && <span className="picker-lock">{rung.at}</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="picker-row">
        <span className="picker-label">Цвет</span>
        <div className="picker">
          {Array.from({ length: COLOR_COUNT }, (_, index) => index + 1).map((color) => {
            const locked = color > openColors;
            const rung = rungOf('color', color);
            const justOpened = reached?.kind === 'color' && reached.index === color;
            const shaking = shake?.startsWith(`color${color}:`) === true;
            return (
              <button
                key={color}
                className={
                  [
                    'picker-swatch',
                    color === draft.avatar.color ? 'picker-swatch--picked' : null,
                    locked ? 'picker-swatch--locked' : null,
                    justOpened ? 'picker-swatch--flash' : null,
                  ]
                    .filter((name) => name !== null)
                    .join(' ')
                }
                aria-label={colorLabel(color, locked ? rung : undefined)}
                aria-pressed={color === draft.avatar.color}
                disabled={busy}
                onClick={() =>
                  locked ? refuse('color', color) : choose({ avatar: { ...draft.avatar, color } })
                }
              >
                <span
                  className={
                    [
                      'picker-swatch-disc',
                      shaking ? 'picker-move--shake' : null,
                      justOpened ? 'picker-move--pop' : null,
                    ]
                      .filter((name) => name !== null)
                      .join(' ')
                  }
                  key={shaking ? (shake as string) : 'still'}
                  // A locked swatch shows no colour at all: a disc drawn in the
                  // tone it would give, however dimmed, is that tone as far as
                  // anybody looking at it can tell, and then it is not a reward.
                  style={locked ? undefined : { background: `var(--avatar-${color})` }}
                >
                  {locked && rung !== undefined && rung.at}
                </span>
              </button>
            );
          })}
        </div>
        {/* What the dashes are, and what the face is for. The first sentence
            goes when there is nothing left dashed - a rule about a state that
            no longer exists is furniture - and the second stands in both, under
            the last of the two grids rather than at the end of the screen: it
            is the answer to what the two rows above are worth doing. The regent
            is the regent; telling him what the regent sees was the app talking
            past the person holding it. */}
        <p className="hint">
          {openEyes === EYES_COUNT && openColors === COLOR_COUNT
            ? 'Всё открыто. '
            : `Пунктирные клетки открывает серия подряд — цифра в углу говорит, какая. Сейчас серия ${streak}, рекорд ${best}. `}
          {choir.role === 'regent'
            ? 'Так тебя видно в списке хора и в посещаемости.'
            : 'Так тебя видит регент в списке хора и в посещаемости.'}
        </p>
      </div>

      {/* The field is the roster-keeper's since 2026-09-08, and for everybody
          else the name is a fact in the group below. Not a disabled input: the
          part and the sex went the same way on 2026-08-24, and the sentence
          that settled it was that a control which saves nothing reads as
          broken. */}
      {choir.keeps_the_roster ? (
        <label className="picker-row">
          <span className="picker-label">Имя</span>
          <span className="field">
            <input
              value={draft.name}
              maxLength={100}
              placeholder="Как тебя записать"
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              // On the blur and not on every keystroke: a save per letter is a
              // request per letter. The guard is the one the way out uses - a
              // blank field is not an instruction to erase the name, and a name
              // that never changed has nothing to send. Without it every tap from
              // this field onto a swatch would fire two saves at once, and the
              // slower of the two would put the old colour back.
              onBlur={() => {
                const trimmed = draft.name.trim();
                if (trimmed === '' || trimmed === savedName.current) return;
                void save(draft);
              }}
            />
          </span>
          {/* The refusal under the field it is about, since 2026-09-01. It stood
              at the foot of the screen while there was a «Готово» down there to
              explain why the screen had not closed; with the button gone, the
              name is the only thing here a save can be refused over. */}
          {failure !== null && <p className="failure">{failure}</p>}
        </label>
      ) : (
        // With no field on the screen a refusal is about the date or the face,
        // and it keeps the place the name's one had: directly above the wheel
        // the next tap lands on.
        failure !== null && <p className="failure">{failure}</p>
      )}

      {/* The date under the name, which is the owner's order rather than the one
          the fields were in. Folded shut: the reason is in BirthdayWheel. */}
      <BirthdayWheel
        frame="block"
        value={draft.birthday}
        onChange={(birthday) => pick({ birthday })}
      />

      {/* The part and the sex, which are the regent's record since 2026-08-24
          and read-only here. One group of two lines and not two blocks of their
          own: as separate rows each carried a label over a half-empty line, and
          what they have in common - that they are read rather than picked - was
          said nowhere. Under one label it is said once, by the label. */}
      <div className="block block--rows">
        <span className="picker-label">Записал регент</span>

        {/* The name joins the two on 2026-09-08, and stands first because it is
            the one of the three a person came here to change. Kept as a row
            rather than left to the heading above: the heading says whose card
            this is, and the group says who decides what is on it. */}
        {!choir.keeps_the_roster && (
          <span className="fact-row">
            <span>Имя</span>
            <span className="picker-fact">{choir.display_name}</span>
          </span>
        )}

        {/* The four voices used to stand here as segments. They went when the
            field became the regent's: a control that saved nothing would be
            worse than the fact standing on its own. */}
        <span className="fact-row">
          <span>Партия</span>
          <span className="picker-fact">
            {choir.voice_part === null ? 'назначит регент' : voicePartName(choir.voice_part)}
          </span>
        </span>

        {/* Stated once at the door, where it is required, and corrected by the
            regent after - so what is left here is the line that says what it
            is. */}
        <span className="fact-row">
          <span>Пол</span>
          <span className="picker-fact">
            {draft.isBrother === null ? 'укажет регент' : draft.isBrother ? 'мужской' : 'женский'}
          </span>
        </span>

        <p className="hint">
          {choir.keeps_the_roster
            ? 'Их читают, а не выбирают: партию ставит регент в списке хора. '
            : 'Их читают, а не выбирают: имя и партию ставит регент в списке хора. '}
          Братья встают в очередь проповедей, больше пол нигде не показывается.
        </p>
      </div>
    </section>
  );
}

/**
 * Whether the reminder reaches this person, at the foot of their own screen.
 *
 * Its own component because it belongs at the bottom while the profile belongs
 * at the top, with the attendance between them.
 *
 * One shape, drawn only where the permission is missing: the washed banner with
 * «Разрешить» in it. Where it is granted there is nothing left to decide, and
 * since 2026-10-06 nothing is drawn - the line that stood there promised a
 * three-hour reminder a choir no longer gets by default (C-001, FND-1587).
 *
 * The «Выключить» that used to stand there is gone by the owner's call of
 * 2026-08-24 - a singer who turns the reminder off is a singer who stops coming
 * for reasons the app then cannot see. Nothing is trapped by this: muting or
 * blocking the bot in Telegram stops its messages, and it is Telegram's own
 * control rather than a copy of it here.
 *
 * Which also settles the objection the previous revision was written for - a
 * control drawn on that does not move reads as broken. There is no control.
 */
export function RemindersRow({ choir }: { choir: ChoirBrief }) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [remindersOn, setRemindersOn] = useState(choir.allows_write_to_pm);

  async function allowReminders(): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      // Ask Telegram again and store the real answer rather than an assumed
      // one: the person may decline a second time as well.
      const allowed = await askWriteAccess();
      await api.setWriteAccess(allowed);
      setRemindersOn(allowed);
      if (!allowed) {
        setFailure('Telegram не дал разрешение. Напоминания приходить не будут.');
      }
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'Не удалось запросить разрешение');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/* The ask, where there is one: the accent wash and the filled button
          belong to the thing that still needs an answer. */}
      {/* Granted draws nothing, and the banner names no lead time: since
          2026-10-06 a choir has no three-hour reminder unless its regent sets
          one, so «за 3 часа» would promise what may not come (C-001, FND-1587). */}
      {!remindersOn && (
        <section className="banner card--toned">
          <p className="banner-text">Напоминания — в личку от бота</p>
          <button className="banner-btn" disabled={busy} onClick={() => void allowReminders()}>
            Разрешить
          </button>
        </section>
      )}
      {failure !== null && <p className="failure">{failure}</p>}
    </>
  );
}
